import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, PageHeader } from './AdminUI';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';
import { API_BASE } from '../../config/api';

const TeamManagement = () => {
    const { apiCall } = useAuth();
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        title: '',
        subtitle: '',
        image: 'images/about/default.jpg',
        email: '',
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        bio: '',
        expertise: [],
        order: 0,
        active: true
    });
    const [expertiseInput, setExpertiseInput] = useState('');
    const [imageFile, setImageFile] = useState(null);

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        try {
            const response = await apiCall('/team');
            const data = await response.json();
            setTeam(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching team:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingMember ? `/team/${editingMember.id}` : '/team';
            const method = editingMember ? 'PUT' : 'POST';

            const fd = new FormData();
            fd.append('name', formData.name);
            fd.append('title', formData.title);
            fd.append('subtitle', formData.subtitle);
            fd.append('email', formData.email);
            fd.append('linkedin', formData.linkedin);
            fd.append('twitter', formData.twitter);
            fd.append('bio', formData.bio);
            fd.append('expertise', JSON.stringify(formData.expertise));
            fd.append('order', String(formData.order));
            fd.append('active', String(formData.active));
            if (imageFile) {
                fd.append('image', imageFile);
            }

            const response = await apiCall(url, { method, body: fd });

            if (response.ok) {
                await fetchTeam();
                handleCloseModal();
                setImageFile(null);
                alertSuccess('Saved', 'Team member saved successfully.');
            } else {
                const result = await response.json();
                alertError('Error', result.message || 'Error saving team member');
            }
        } catch (error) {
            console.error('Error saving team member:', error);
            alertError('Error', 'Error saving team member');
        }
    };

    const handleEdit = (member) => {
        setEditingMember(member);
        setImageFile(null);
        setFormData({
            name: member.name,
            title: member.title,
            subtitle: member.subtitle,
            image: member.image,
            email: member.email,
            linkedin: member.linkedin,
            twitter: member.twitter,
            bio: member.bio,
            expertise: Array.isArray(member.expertise) ? member.expertise : [],
            order: member.order,
            active: member.active
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete team member?', text: 'Are you sure? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/team/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchTeam();
                alertSuccess('Deleted', 'Team member deleted.');
            } else {
                alertError('Error', 'Error deleting team member');
            }
        } catch (error) {
            console.error('Error deleting team member:', error);
            alertError('Error', 'Error deleting team member');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingMember(null);
        setFormData({
            name: '',
            title: '',
            subtitle: '',
            image: 'images/about/default.jpg',
            email: '',
            linkedin: 'https://linkedin.com',
            twitter: 'https://twitter.com',
            bio: '',
            expertise: [],
            order: 0,
            active: true
        });
        setExpertiseInput('');
        setImageFile(null);
    };

    const handleToggleActive = async (member) => {
        try {
            const response = await apiCall(`/team/${member.id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...member, active: !member.active })
            });
            if (response.ok) {
                await fetchTeam();
            }
        } catch (error) {
            console.error('Error toggling active status:', error);
        }
    };

    const handleAddExpertise = () => {
        if (expertiseInput.trim() && !formData.expertise.includes(expertiseInput.trim())) {
            setFormData({ ...formData, expertise: [...formData.expertise, expertiseInput.trim()] });
            setExpertiseInput('');
        }
    };

    const handleRemoveExpertise = (itemToRemove) => {
        setFormData({ ...formData, expertise: formData.expertise.filter(item => item !== itemToRemove) });
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading team members..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Team Members"
                    subtitle="Manage your team profiles"
                    action={
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary"
                    >
                        <i className="fa fa-plus"></i>
                        Add Team Member
                    </button>
                    }
                />

                <div className="admin-card-grid">
                    {team.length === 0 ? (
                        <div style={{ gridColumn: '1/-1' }}>
                            <EmptyState
                                icon="fa fa-users"
                                title="No team members yet"
                                text="Add your first team member to build the public team page."
                            />
                        </div>
                    ) : (
                        team.map((member) => (
                            <div key={member.id} style={{
                                background: 'white',
                                borderRadius: '0',
                                overflow: 'hidden',
                                border: member.active ? '2px solid #10b981' : '2px solid #e2e8f0'
                            }}>
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={member.image?.startsWith('uploads/') ? `${API_BASE.replace(/\/api\/?$/, '')}/${member.image}` : (member.image ? (process.env.PUBLIC_URL + (member.image.startsWith('/') ? '' : '/') + member.image) : '')}
                                        alt={member.name}
                                        style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '8px',
                                        background: member.active ? '#10b981' : '#9ca3af',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '0',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        {member.active ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                                <div style={{ padding: '16px' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '4px' }}>
                                        {member.name}
                                    </h3>
                                    <p style={{ fontSize: '14px', color: '#62B6E8', marginBottom: '12px' }}>
                                        {member.title}
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px', lineHeight: '1.5' }}>
                                        {member.bio.substring(0, 100)}...
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => handleEdit(member)}
                                            style={{
                                                flex: 1,
                                                padding: '8px',
                                                background: '#62B6E8',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fa fa-edit"></i> Edit
                                        </button>
                                        <button
                                            onClick={() => handleToggleActive(member)}
                                            style={{
                                                padding: '8px 12px',
                                                background: member.active ? '#f59e0b' : '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className={`fa fa-${member.active ? 'eye-slash' : 'eye'}`}></i>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(member.id)}
                                            style={{
                                                padding: '8px 12px',
                                                background: '#e53e3e',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Modal */}
                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '700px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    {editingMember ? 'Edit Team Member' : 'Add Team Member'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Subtitle *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.subtitle}
                                        onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Photo
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                                        <div style={{
                                            width: '120px',
                                            height: '120px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            overflow: 'hidden',
                                            background: '#f7fafc',
                                            flexShrink: 0
                                        }}>
                                            {(imageFile || formData.image) ? (
                                                <img
                                                    src={imageFile ? URL.createObjectURL(imageFile) : (formData.image.startsWith('http') ? formData.image : (formData.image.startsWith('uploads/') ? `${API_BASE.replace(/\/api\/?$/, '')}/${formData.image}` : (process.env.PUBLIC_URL + '/' + formData.image)))}
                                                    alt="Preview"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%23e2e8f0" width="120" height="120"/%3E%3Ctext fill="%239ca3af" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="12"%3ENo image%3C/text%3E%3C/svg%3E'; }}
                                                />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}>
                                                    No image
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <input
                                                type="file"
                                                accept=".jpg,.jpeg,.png,.gif,.webp"
                                                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '0',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#718096' }}>
                                                {editingMember && !imageFile ? 'Choose a new file to replace the current photo.' : 'JPG, PNG, GIF or WebP. Max 5MB.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            LinkedIn
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.linkedin}
                                            onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Twitter
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.twitter}
                                            onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Bio *
                                    </label>
                                    <textarea
                                        value={formData.bio}
                                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                        required
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Expertise Areas
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                        <input
                                            type="text"
                                            value={expertiseInput}
                                            onChange={(e) => setExpertiseInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExpertise())}
                                            style={{
                                                flex: 1,
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                            placeholder="Add expertise and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddExpertise}
                                            style={{
                                                padding: '10px 20px',
                                                background: '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {formData.expertise.map((item) => (
                                            <span
                                                key={item}
                                                style={{
                                                    background: '#d1fae5',
                                                    color: '#065f46',
                                                    padding: '4px 12px',
                                                    borderRadius: '0',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {item}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveExpertise(item)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46' }}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Active (visible on website)</span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#e2e8f0',
                                            color: '#4a5568',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {editingMember ? 'Update' : 'Add'} Team Member
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default TeamManagement;
